<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, onMounted, ref, type ComputedRef, type Ref } from "vue";
import {
  DATE_TIME_FORMAT_OPTIONS,
  type DateTimeFormatOption,
  type DateTimeFormatPreference,
  type DashboardDefaultPreset,
} from "../api/user-settings.js";
import AppShell from "../components/AppShell.vue";
import { formatDateTime } from "../formatting/date-time.js";
import { useUserSettingsStore } from "../stores/user-settings.js";

const userSettingsStore: ReturnType<typeof useUserSettingsStore> = useUserSettingsStore();
const selectedDateTimeFormat: Ref<DateTimeFormatPreference> = ref(
  userSettingsStore.settings.dateTimeFormat,
);
const saving: Ref<boolean> = ref(false);
const selectedDashboardDefaultPreset: Ref<DashboardDefaultPreset> = ref(userSettingsStore.settings.dashboardDefaultPreset);
const selectedDashboardDefaultResolution: Ref<string> = ref(userSettingsStore.settings.dashboardDefaultResolution);
const message: Ref<string | null> = ref(null);
const failed: Ref<boolean> = ref(false);
const sampleTimestamp: string = "2026-08-09T17:45:30.000Z";

const currentOption: ComputedRef<DateTimeFormatOption | null> = computed(
  (): DateTimeFormatOption | null =>
    DATE_TIME_FORMAT_OPTIONS.find(
      (option: DateTimeFormatOption): boolean => option.value === selectedDateTimeFormat.value,
    ) ?? null,
);
const formattedPreview: ComputedRef<string> = computed(
  (): string => formatDateTime(sampleTimestamp, selectedDateTimeFormat.value),
);

async function load(): Promise<void> {
  try {
    await userSettingsStore.initialize();
    selectedDateTimeFormat.value = userSettingsStore.settings.dateTimeFormat;
    selectedDashboardDefaultPreset.value = userSettingsStore.settings.dashboardDefaultPreset;
    selectedDashboardDefaultResolution.value = userSettingsStore.settings.dashboardDefaultResolution;
  } catch (error: unknown) {
    failed.value = true;
    message.value = error instanceof Error ? error.message : "User settings could not be loaded.";
  }
}

async function save(): Promise<void> {
  saving.value = true;
  failed.value = false;
  message.value = null;
  try {
    await userSettingsStore.updateSettings(selectedDateTimeFormat.value, selectedDashboardDefaultPreset.value, selectedDashboardDefaultResolution.value);
    message.value = "User settings saved.";
  } catch (error: unknown) {
    failed.value = true;
    message.value = error instanceof Error ? error.message : "User settings could not be saved.";
  } finally {
    saving.value = false;
  }
}

onMounted(load);
</script>

<template>
  <AppShell>
    <header class="page-heading">
      <div>
        <p class="eyebrow">Account</p>
        <h1>User settings</h1>
        <p>Personal UI preferences stored in your server-side user profile.</p>
      </div>
    </header>

    <section class="content-panel settings-card">
      <div class="settings-card__heading">
        <div>
          <p class="eyebrow">Display</p>
          <h2>Date and time format</h2>
        </div>
      </div>

      <form class="settings-form user-settings-form" @submit.prevent="save">
        <label class="settings-field--wide">
          <span>Time format</span>
          <select v-model="selectedDateTimeFormat">
            <option
              v-for="option in DATE_TIME_FORMAT_OPTIONS"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>

        <div class="notice settings-field--wide">
          <strong>{{ currentOption?.label }}</strong>
          <p>{{ currentOption?.description }}</p>
          <p>Preview: {{ formattedPreview }}</p>
        </div>
        <label><span>Dashboard default</span><select v-model="selectedDashboardDefaultPreset"><option value="live">Live</option><option value="today">Today</option><option value="this_week">This week</option><option value="this_month">This month</option><option value="last_month">Last month</option><option value="this_year">This year</option><option value="last_year">Last year</option></select></label>
        <label><span>Dashboard resolution</span><input v-model="selectedDashboardDefaultResolution" type="text" pattern="[1-9][0-9]*(m|h|d)" required></label>

        <div class="settings-actions settings-field--wide">
          <p v-if="message" :class="failed ? 'form-error' : 'settings-message'" :role="failed ? 'alert' : 'status'">
            {{ message }}
          </p>
          <div>
            <button class="button button--primary" type="submit" :disabled="saving">
              {{ saving ? "Saving…" : "Save user settings" }}
            </button>
          </div>
        </div>
      </form>
    </section>
  </AppShell>
</template>
